import { Box, Stack } from '@mui/material';
import type { ReactNode, Ref } from 'react';

import { layout } from '@/theme';

/**
 * The scroll owner inside a panel, ported from
 * Web/src/components/layouts/PanelContent/.
 *
 * One component owning the scroll is the point. Before this existed each page
 * hand-rolled `flex: 1; minWidth: 0; overflowY: auto` and separated its sections
 * with an `8px solid var(--bg-app)` bottom border standing in for a gap. The
 * inner `Stack gap={2}` here is the design system's 16px section rhythm, so
 * sections just stack and nothing needs a margin.
 */
export type PanelContentProps = {
  children: ReactNode;
  /** Let an inner element own the scroll instead — for virtualised lists and tables. */
  hideOverflow?: boolean;
  /** 64px of breathing room under the last section. Off for full-height tables. */
  hasPaddingBottom?: boolean;
  /** Caps the content column, for prose-width pages. */
  constrainWidth?: boolean;
  ref?: Ref<HTMLDivElement>;
  'data-automation-id'?: string;
};

export const PanelContent = ({
  children,
  hideOverflow = false,
  hasPaddingBottom = true,
  constrainWidth = false,
  ref,
  'data-automation-id': automationId,
}: PanelContentProps) => (
  <Box
    ref={ref}
    // -1 so Escape-to-collapse can find focus inside the drawer, and so the
    // panel itself can be focused programmatically without entering the tab order.
    tabIndex={-1}
    data-automation-id={automationId}
    sx={{
      width: '100%',
      height: '100%',
      overflow: hideOverflow ? 'hidden' : 'auto',
      /*
       * This is the white card, and it needs all three properties. The surfaces
       * are three layers deep, matching Web's Project Portfolio:
       *
       *   AppShell PageWrapper  white   — visible on routes with no Panels
       *     PanelGroup          grey    — the gutter between panels
       *       PanelContent      white   — one card per panel, this component
       *
       * Do not "de-duplicate" the white against PageWrapper: the grey PanelGroup
       * sits between them, so removing it here leaves the whole content area grey
       * and makes the 2px resize handle invisible, since the handle only reads as
       * a line where grey shows between two white cards.
       */
      bgcolor: 'var(--color-black-00)',
      borderRadius: 'var(--border-radius-medium)',
      border: 'var(--border-width-hairline) solid var(--border-divider)',
      p: 'var(--spacing-unit-3)',
      pb: hasPaddingBottom ? 'var(--spacing-unit-8)' : 'var(--spacing-unit-3)',
      '&:focus-visible': {
        outline: '2px solid var(--fg-3)',
        outlineOffset: '-2px',
      },
      // Safari's elastic scroll lets sticky children drift and open gaps against
      // the rounded border; the design system pins it for the same reason.
      '@supports (-webkit-appearance: none) and (stroke-color: transparent)': {
        overscrollBehaviorY: 'none',
      },
    }}
  >
    <Stack
      gap={2}
      sx={{
        ...(hideOverflow ? { height: '100%' } : {}),
        ...(constrainWidth ? { width: '100%', maxWidth: layout.contentMaxWidth } : {}),
      }}
    >
      {children}
    </Stack>
  </Box>
);
